'use client';

import { AnimatePresence, motion } from 'framer-motion';
import React, { Fragment, Suspense, useState } from 'react';
import Image from 'next/image';

const ScrollableIcons = ({ data }) => {
    const keys = Object.keys(data);
    const [activeTab, setActiveTab] = useState(keys[0]);

    const tabVariants = {
        hidden: { opacity: 0, scale: 0.9, y: 20 },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: { duration: 0.4, ease: 'easeOut' },
        },
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.08,
                delayChildren: 0.1,
            },
        },
    };

    const imageVariants = {
        hidden: { opacity: 0, scale: 0.95, y: 30 },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: { duration: 0.6, ease: 'easeOut' },
        },
        exit: {
            opacity: 0,
            scale: 0.97,
            transition: { duration: 0.3 },
        },
    };

    return (
        <Fragment>
            {/* ===== SCROLLABLE ICONS ===== */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={staggerContainer}
                className="
                    flex gap-4 md:gap-6
                    px-4 py-4
                    md:justify-center
                    overflow-x-auto
                    md:overflow-visible
                    whitespace-nowrap
                    snap-x snap-mandatory
                "
            >
                {keys.map((elm) => (
                    <motion.div
                        key={elm}
                        variants={tabVariants}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setActiveTab(elm)}
                        className={`
                            min-w-[120px] md:min-w-[160px]
                            snap-center
                            cursor-pointer
                            rounded-lg
                            px-5 py-4
                            text-center
                            transition-all duration-300
                            ${
                                activeTab === elm
                                    ? 'text-[#2e2e2e] font-bold shadow-lg shadow-blue-100'
                                    : 'text-gray-600 opacity-70'
                            }
                        `}
                    >
                        <motion.div
                            className="mb-2 flex justify-center"
                            animate={{
                                rotate:
                                    activeTab === elm
                                        ? [0, -10, 10, -10, 0]
                                        : 0,
                            }}
                            transition={{ duration: 0.5 }}
                        >
                            <i className={`ri-${data[elm].icon} text-3xl`}></i>
                        </motion.div>
                        <p className="text-sm md:text-base">{elm}</p>
                    </motion.div>
                ))}
            </motion.div>

            {/* ===== IMAGE SECTION ===== */}
            <div className="flex justify-center items-center px-4 md:px-0 py-8 md:py-20">
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-4xl"
                >
                    <Suspense
                        fallback={
                            <div className="flex animate-pulse justify-center">
                                <div className="h-40 w-40 rounded-lg bg-gray-300" />
                            </div>
                        }
                    >
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                variants={imageVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                className="flex justify-center"
                            >
                                <Image
                                    src={data[activeTab].image}
                                    alt={`Location ${activeTab}`}
                                    width={600}
                                    height={400}
                                    className="
                                        w-full
                                        max-w-full
                                        rounded-lg
                                        shadow-xl
                                        shadow-blue-100
                                    "
                                />
                            </motion.div>
                        </AnimatePresence>
                    </Suspense>
                </motion.div>
            </div>
        </Fragment>
    );
};

export default ScrollableIcons;
